import { immerable } from "immer"
import { Pose } from "../Pose.ts"
import { Decoder } from "../Coding.ts"
import { nullSkeleton, Skeleton } from "../Mannequin.tsx"

/**
 * A `Timeline` is a sequence of keyframes. A `Timeline` always has at least two keyframes. The first keyframe's time is always zero and the last keyframe's time defines the duration of the timeline.
 */
export class Timeline {
  readonly [immerable] = true

  constructor(
    /** The name of the model this `Timeline` was created for. I can use this to preserve the timeline across reloads. */
    readonly modelName: string,

    // Invariant: keyframes.length ≥ 2
    // Invariant: all keyframe times are unique
    // Invariant: keyframes[0].time === 0
    // Invariant: keyframes are sorted by time
    readonly keyframes: Keyframe[],
  ) {
    if (keyframes.length < 2) {
      throw new Error("invariant broken: timeline has at least two keyframes")
    }
    if (keyframes[0].time !== 0) {
      throw new Error("invariant broken: first keyframe time === 0")
    }
    keyframes.forEach((kf, i) => {
      if (i === 0) {
        return
      }
      if (keyframes[i - 1].time >= kf.time) {
        throw new Error("invariant broken: keyframes are sorted by time with all timestamps unique")
      }
    })
  }

  static makeInitialTimeline(skeleton: Skeleton, modelName: string): Timeline {
    const pose = Pose.forSkeleton(skeleton)
    return new Timeline(modelName, [
      { time: 0, pose },
      { time: 1, pose },
    ])
  }

  static decodeFrom(decoder: Decoder): Timeline {
    const container = decoder.keyedDecoder()
    return new Timeline(
      container.decodeString('modelName', 'null'),
      container.decodeArray('keyframes', decodeKeyframe),
    )
  }

  isCompatibleWith(skeleton: Skeleton, modelName: string): boolean {
    if (skeleton === nullSkeleton) {
      return true
    }
    return this.modelName === modelName && this.keyframes[0].pose.isCompatibleWith(skeleton)
  }

  get duration(): number { return this.keyframes[this.keyframes.length - 1].time }

  poseAtTime(when: number): Pose {
    const detail = this.interpolationParameters(when)
    return Pose.interpolate(detail.earlyKeyframe.pose, detail.lateKeyframe.pose, detail.t)
  }

  private interpolationParameters(when: number): InterpolationParameters {
    // This could be turned into a binary search for efficiency.
    let lateIndex = this.keyframes.findIndex(kf => kf.time >= when)
    if (lateIndex === -1) {
      // when is later than the last keyframe's time.
      return {
        earlyKeyframe: this.keyframes[this.keyframes.length - 1],
        lateKeyframe: this.keyframes[this.keyframes.length - 1],
        t: 0,
      }
    }

    if (lateIndex === 0) {
      return {
        earlyKeyframe: this.keyframes[lateIndex],
        lateKeyframe: this.keyframes[lateIndex],
        t: 0,
      }
    }

    const earlyKeyframe = this.keyframes[lateIndex - 1]
    const lateKeyframe = this.keyframes[lateIndex]
    return {
      earlyKeyframe,
      lateKeyframe,
      t: (when - earlyKeyframe.time) / (lateKeyframe.time - earlyKeyframe.time),
    }
  }

  /** Ensure there is a keyframe at time `when`.
   * 
   * @returns A `Timeline` with a keyframe at `when`, and the index of that keyframe.
   */
  withKeyframeAtTime(when: number): [Timeline, number] {
    when = Math.max(0, when)

    const index = this.keyframes.findIndex(kf => kf.time >= when)
    if (index === -1) {
      // when is later than the last keyframe's time.
      const pose = this.keyframes[this.keyframes.length - 1].pose
      const newKeyframe = {
        time: when,
        pose,
      }
      const newTimeline = new Timeline(
        this.modelName,
        [...this.keyframes, newKeyframe],
      )
      return [newTimeline, newTimeline.keyframes.length - 1]
    }

    if (this.keyframes[index].time === when) {
      // Maintain invariant: all times are unique.
      return [this, index]
    }

    const newKeyframe = {
      time: when,
      pose: this.poseAtTime(when),
    }
    const newTimeline = new Timeline(
      this.modelName,
      [
        ...this.keyframes.slice(0, index),
        newKeyframe,
        ...this.keyframes.slice(index),
      ]
    )
    return [newTimeline, index]
  }

  canDeleteKeyframeAtTime(time: number): boolean {
    if (this.keyframes.length <= 2) {
      return false
    }
    return this.keyframes.find(kf => kf.time === time) !== undefined
  }

  withoutKeyframeAtTime(when: number): Timeline {
    if (this.keyframes.length <= 2) {
      // Don't break the invariant: at least two keyframes.
      return this
    }

    const index = this.keyframes.findIndex(kf => kf.time === when)

    if (index === -1) {
      // when is not in the timeline.
      return this
    }

    if (index === 0) {
      // Deleting the first keyframe requires shifting all later keyframes back in time.
      const shift = this.keyframes[1].time
      const newKeyframes = this.keyframes.slice(1).map(kf => ({
        time: kf.time - shift,
        pose: kf.pose,
      }))
      return new Timeline(this.modelName, newKeyframes)
    }

    return new Timeline(
      this.modelName,
      [
        ...this.keyframes.slice(0, index),
        ...this.keyframes.slice(index + 1),
      ]
    )
  }

  /// Duplicate the final keyframe at a later time to lengthen the timeline.
  withTimelineExtendedBySeconds(seconds: number): Timeline {
    const oldFinal = this.keyframes[this.keyframes.length - 1]
    const newFinal = {
      time: oldFinal.time + seconds,
      pose: oldFinal.pose,
    }
    return new Timeline(
      this.modelName,
      [...this.keyframes, newFinal],
    )
  }
}

interface InterpolationParameters {
  /// The latest keyframe no later than the current time.
  earlyKeyframe: Keyframe

  /// The earliest keyframe no earlier than the current time.
  lateKeyframe: Keyframe

  /// The interpolation parameter: 0 = earlyKeyframe, 1 = lateKeyframe.
  t: number
}

export interface Keyframe {
  time: number
  pose: Pose
}

function decodeKeyframe(decoder: Decoder): Keyframe {
  const container = decoder.keyedDecoder()
  return {
    time: container.decodeNumber('time'),
    pose: container.decodeInstance('pose', Pose.decodeFrom),
  }
}
